import { ArrowRightLeft, Boxes, Database, Layers3, Rocket } from 'lucide-react';
import type {
  ReleaseArtifactRow,
  ReleaseChannelRow,
  ReleasePromotionRow,
  RuntimeConfigRow
} from '../api';
import type {
  ActivityOption,
  MetricOption,
  ProjectOption,
  ServiceOption
} from '../types/navigation';
import {
  formatArtifactSummary,
  formatRelativeDate,
  toneFromBoolean
} from './formatters';

export function buildWorkspaceMetrics(
  artifacts: ReleaseArtifactRow[],
  channels: ReleaseChannelRow[],
  runtimeConfigs: RuntimeConfigRow[],
  sortedPromotions: ReleasePromotionRow[]
): MetricOption[] {
  const servingChannels = channels.filter(
    (channel) => channel.isActive && Boolean(channel.releaseArtifactId)
  );
  const deployedArtifactIds = new Set(
    servingChannels
      .map((channel) => channel.releaseArtifactId)
      .filter((id): id is string => Boolean(id))
  );
  const activeRuntimeConfigs = runtimeConfigs.filter(
    (config) => config.isActive
  );

  return [
    {
      label: 'Serving targets',
      value: `${servingChannels.length}/${channels.length}`,
      helper: 'Exact targets currently delivering a bundle',
      icon: Layers3,
      tone: 'green'
    },
    {
      label: 'Bundles ready',
      value: `${Math.max(artifacts.length - deployedArtifactIds.size, 0)}`,
      helper: 'Uploaded bundles not currently serving',
      icon: Rocket,
      tone: 'blue'
    },
    {
      label: 'Runtime rules',
      value: `${activeRuntimeConfigs.length}`,
      helper: 'Active rules eligible to resolve',
      icon: Database,
      tone: 'amber'
    },
    {
      label: 'Last deployment',
      value: sortedPromotions[0]
        ? formatRelativeDate(sortedPromotions[0].created)
        : '—',
      helper: 'Most recent target change',
      icon: ArrowRightLeft,
      tone: 'violet'
    }
  ];
}

export function buildWorkspaceActivity(
  sortedPromotions: ReleasePromotionRow[],
  artifactById: Map<string, ReleaseArtifactRow>,
  sortedArtifacts: ReleaseArtifactRow[]
): ActivityOption[] {
  const promotionRows = sortedPromotions.slice(0, 3).map((promotion) => {
    const artifact = artifactById.get(promotion.releaseArtifactId);
    return {
      title: `${promotion.environment}/${promotion.channel} deployed`,
      subtitle: `${formatArtifactSummary(artifact)} · ${formatRelativeDate(promotion.created)}`,
      status: promotion.actorType === 'api_key' ? 'API' : 'User',
      tone: 'success' as const,
      icon: ArrowRightLeft
    };
  });
  if (promotionRows.length > 0) return promotionRows;

  return sortedArtifacts.slice(0, 3).map((artifact) => ({
    title: `${formatArtifactSummary(artifact)} uploaded`,
    subtitle: `OTA bundle · ${formatRelativeDate(artifact.created)}`,
    status: artifact.status.toUpperCase(),
    tone: toneFromBoolean(artifact.status === 'promoted'),
    icon: Boxes
  }));
}

export function buildWorkspaceServices(
  apps: ProjectOption[],
  artifactById: Map<string, ReleaseArtifactRow>,
  channels: ReleaseChannelRow[],
  sortedArtifacts: ReleaseArtifactRow[]
): ServiceOption[] {
  return apps.map((app) => {
    const appArtifacts = sortedArtifacts.filter(
      (artifact) => artifact.projectId === app.id
    );
    const latestArtifact = appArtifacts[0];
    const appChannels = channels.filter(
      (channel) => channel.projectId === app.id
    );
    const servingChannel = appChannels.find(
      (channel) => channel.isActive && channel.releaseArtifactId
    );
    const servingArtifact = servingChannel?.releaseArtifactId
      ? artifactById.get(servingChannel.releaseArtifactId)
      : undefined;

    return {
      name: app.name,
      status: servingArtifact
        ? 'Serving'
        : latestArtifact
          ? 'Bundle ready'
          : 'No bundle',
      tone: servingArtifact ? 'success' : latestArtifact ? 'warn' : 'neutral',
      environment: servingChannel?.environment || 'unassigned',
      deployment: servingArtifact
        ? `${servingArtifact.version} · ${formatRelativeDate(servingArtifact.created)}`
        : latestArtifact
          ? 'Uploaded, not deployed'
          : 'No bundle uploaded'
    };
  });
}
